import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormField, FormItem, FormLabel, FormDescription, FormMessage, FormControl } from '@/shared/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { BaseFieldProps } from './types';
import { EmailTemplateService } from '@/features/Settings/services/emailTemplateService';
import { EmailTemplatePreviewModal } from '@/features/Settings/components/EmailTemplatePreviewModal';
import type { AvailableTemplate } from '@/features/Settings/types/EmailTemplateTypes';

const INTERNAL_KEYS = new Set([
  'nodeId', 'executionId', 'organizationId', 'workspaceId',
  'agentBaseId', 'parameters', 'prompts', '_webhook',
]);

function resolveUserLanguage(i18nLang: string): string {
  const primary = i18nLang.split('-')[0].toLowerCase();
  const map: Record<string, string> = { en: 'en-US', pt: 'pt-BR', es: 'es-ES', fr: 'fr-FR', de: 'de-DE' };
  return map[primary] ?? i18nLang;
}

interface EmailTemplateSelectorFieldProps extends BaseFieldProps {}

export const EmailTemplateSelectorField: React.FC<EmailTemplateSelectorFieldProps> = ({
  field,
  name,
  control,
  formValues,
  nodeInputData,
}) => {
  const { setValue } = useFormContext();
  const { t, i18n } = useTranslation();
  const userLanguage = resolveUserLanguage(i18n.language);

  const [showDetails, setShowDetails] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [activeLanguage, setActiveLanguage] = useState<string>(
    formValues?.template_language || userLanguage
  );

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['email-templates', 'available'],
    queryFn: () => EmailTemplateService.getAvailableTemplates(undefined),
  });

  const inputKeys = Object.keys(nodeInputData ?? {}).filter((k) => !INTERNAL_KEYS.has(k));

  // One entry per slug, preferring user language
  const dropdownTemplates = useMemo(() => {
    const bySlug = new Map<string, AvailableTemplate>();
    for (const t of templates) {
      const existing = bySlug.get(t.slug);
      if (!existing || (t.language === userLanguage && existing.language !== userLanguage)) {
        bySlug.set(t.slug, t);
      }
    }
    return Array.from(bySlug.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [templates, userLanguage]);

  const selectedSlug = formValues?.template_slug ?? '';

  const availableLanguages = useMemo(
    () => templates.filter((t) => t.slug === selectedSlug).map((t) => t.language),
    [templates, selectedSlug]
  );

  const getSelectedTemplate = (): AvailableTemplate | undefined => {
    if (!selectedSlug) return undefined;
    return templates.find((t) => t.slug === selectedSlug && t.language === activeLanguage)
      ?? templates.find((t) => t.slug === selectedSlug);
  };

  const buildVars = (m: Record<string, string>): Record<string, string> => {
    const result: Record<string, string> = {};
    for (const [templateKey, inputKey] of Object.entries(m)) {
      if (inputKey && inputKey !== '__CLEAR__') {
        result[templateKey] = `\${input.${inputKey}}`;
      }
    }
    return result;
  };

  const handleMappingChange = (templateKey: string, inputKey: string) => {
    const newMapping = { ...mapping };
    if (inputKey === '__CLEAR__') {
      delete newMapping[templateKey];
    } else {
      newMapping[templateKey] = inputKey;
    }
    setMapping(newMapping);
    setValue('template_variables', buildVars(newMapping));
  };

  const applyAutoMapping = (template: AvailableTemplate) => {
    const autoMapping: Record<string, string> = {};
    for (const v of template.variables) {
      if (inputKeys.includes(v.key)) {
        autoMapping[v.key] = v.key;
      }
    }
    setMapping(autoMapping);
    setValue('template_variables', buildVars(autoMapping));
  };

  const handleLanguageSwitch = (lang: string) => {
    setActiveLanguage(lang);
    setValue('template_language', lang);
    const template = templates.find((t) => t.slug === selectedSlug && t.language === lang);
    if (template) applyAutoMapping(template);
  };

  // Restore mapping from saved form values on mount
  useEffect(() => {
    const saved = formValues?.template_variables;
    if (saved && typeof saved === 'object') {
      const restored: Record<string, string> = {};
      for (const [k, v] of Object.entries(saved as Record<string, string>)) {
        const match = typeof v === 'string' ? v.match(/^\$\{input\.(.+)\}$/) : null;
        if (match) restored[k] = match[1];
      }
      if (Object.keys(restored).length > 0) setMapping(restored);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field: formField }) => {
        const selected = getSelectedTemplate();

        const hasVariables = (selected?.variables?.length ?? 0) > 0;
        const hasInputKeys = inputKeys.length > 0;

        return (
          <FormItem>
            <FormLabel>{field.label}</FormLabel>
            <FormControl>
              <Select
                onValueChange={(value) => {
                  if (value === '__CLEAR__') {
                    formField.onChange('');
                    setValue('template_language', '');
                    setMapping({});
                    setValue('template_variables', {});
                    setActiveLanguage(userLanguage);
                  } else {
                    formField.onChange(value);
                    // Auto-select user language, fallback to first available
                    const preferred = templates.find((t) => t.slug === value && t.language === userLanguage)
                      ?? templates.find((t) => t.slug === value);
                    const lang = preferred?.language ?? userLanguage;
                    setActiveLanguage(lang);
                    setValue('template_language', lang);
                    if (preferred) applyAutoMapping(preferred);
                  }
                  setShowDetails(false);
                }}
                value={selectedSlug || undefined}
                disabled={isLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder={isLoading ? t('workflow.email.loadingTemplates') : t('workflow.email.selectTemplate')} />
                </SelectTrigger>
                <SelectContent>
                  {formField.value && (
                    <SelectItem value="__CLEAR__">{t('workflow.email.noTemplate')}</SelectItem>
                  )}
                  {dropdownTemplates.map((tmpl) => (
                    <SelectItem key={tmpl.slug} value={tmpl.slug}>
                      <span className="flex items-center gap-2">
                        {tmpl.name}
                        {tmpl.source === 'org' && (
                          <Badge variant="secondary" className="text-xs py-0 px-1">
                            {t('workflow.email.customBadge')}
                          </Badge>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
            {field.description && <FormDescription>{field.description}</FormDescription>}
            <FormMessage />

            {/* Language switcher */}
            {selectedSlug && availableLanguages.length > 0 && (
              <div className="mt-1">
                {availableLanguages.length === 1 ? (
                  <p className="text-xs text-muted-foreground">
                    {t('workflow.email.noTranslation')}
                  </p>
                ) : (
                  <div className="flex gap-1 rounded-md border bg-background p-0.5 w-fit">
                    {availableLanguages.map((lang) => (
                      <button
                        key={lang}
                        type="button"
                        onClick={() => handleLanguageSwitch(lang)}
                        className={`rounded px-2 py-0.5 text-xs transition-colors ${
                          activeLanguage === lang
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {lang}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Variable mapper — shown directly when template has variables and previous node is connected */}
            {selected && hasVariables && hasInputKeys && (
              <div className="mt-3 rounded-md border bg-muted/40 p-3 text-sm">
                <p className="font-medium mb-2">{t('workflow.email.mapVariables')}</p>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-1 pr-2 font-medium text-muted-foreground">{t('workflow.email.variable')}</th>
                      <th className="text-left py-1 font-medium text-muted-foreground">{t('workflow.email.workflowField')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selected.variables.map((v) => (
                      <tr key={v.key} className="border-b last:border-0">
                        <td className="py-1 pr-3 font-mono text-xs">{`{{${v.key}}}`}</td>
                        <td className="py-1">
                          <Select
                            value={mapping[v.key] ?? ''}
                            onValueChange={(val) => handleMappingChange(v.key, val)}
                          >
                            <SelectTrigger className="h-7 text-xs">
                              <SelectValue placeholder={t('workflow.email.notMapped')} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__CLEAR__">{t('workflow.email.notMapped')}</SelectItem>
                              {inputKeys.map((k) => (
                                <SelectItem key={k} value={k}>{k}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Template details collapsible */}
            {selected && (
              <div className="mt-2 rounded-md border bg-muted/40 p-3 text-sm space-y-2">
                <button
                  type="button"
                  className="flex w-full items-center justify-between font-medium text-left"
                  onClick={() => setShowDetails((v) => !v)}
                >
                  <span>Detalhes do template</span>
                  {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>

                {showDetails && (
                  <div className="space-y-3 pt-1">
                    <div>
                      <span className="text-muted-foreground">Assunto: </span>
                      <span>{selected.subject}</span>
                    </div>

                    {/* Read-only variables list — only shown when no previous node is connected */}
                    {hasVariables && !hasInputKeys && (
                      <div>
                        <p className="text-muted-foreground mb-1 text-xs">{t('workflow.email.autoFilledVariables')}</p>
                        <table className="w-full text-xs border-collapse">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left py-1 pr-2 font-medium">{t('workflow.email.key')}</th>
                              <th className="text-left py-1 pr-2 font-medium">{t('workflow.email.description')}</th>
                              <th className="text-left py-1 font-medium">{t('workflow.email.example')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selected.variables.map((v) => (
                              <tr key={v.key} className="border-b last:border-0">
                                <td className="py-1 pr-2 font-mono">{`{{${v.key}}}`}</td>
                                <td className="py-1 pr-2">{v.description}</td>
                                <td className="py-1 text-muted-foreground">{v.example}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowPreview(true)}
                    >
                      Pré-visualizar
                    </Button>
                  </div>
                )}
              </div>
            )}

            {showPreview && selected && (
              <EmailTemplatePreviewModal
                template={{
                  id: '',
                  created_at: '',
                  updated_at: '',
                  slug: selected.slug,
                  name: selected.name,
                  subject: selected.subject,
                  content: selected.content,
                  variables: selected.variables,
                  language: selected.language,
                }}
                onClose={() => setShowPreview(false)}
              />
            )}
          </FormItem>
        );
      }}
    />
  );
};

export default EmailTemplateSelectorField;
